import {
  OrderModel,
  OrderStore,
  selectCurrentOrder,
} from '@des/store/order';
import {
  select,
} from '@ngrx/store';
import {
  Observable,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
} from 'rxjs/operators';

/**
 * Returns a method which takes the source store as parameter and extracts the order
 */
export const retrieveCurrentOrder = () => (source: Observable<OrderStore>) =>
  source.pipe(
    filter((store) => !!store.order),
    select(selectCurrentOrder),
    distinctUntilChanged()
  );

/**
 * Returns a method which takes the source store as parameter and extracts the order creation point of sale id
 */
export const retrieveOrderCreationPointOfSaleId = () => (source: Observable<OrderStore>) =>
  source.pipe(
    filter((store) => !!store.order),
    select(selectCurrentOrder),
    map((order) => order?.creationPointOfSale?.pointOfSaleId || ''),
    distinctUntilChanged()
  );

/**
 * Returns a method which takes the source store as parameter and extracts the order servicing point of sale id
 */
export const retrieveOrderServicingPointOfSaleId = () => (source: Observable<OrderStore>) =>
  source.pipe(
    filter((store) => !!store.order),
    select(selectCurrentOrder),
    map((order) => order?.servicingPointOfSale?.pointOfSaleId || ''),
    distinctUntilChanged()
  );

/**
 * Returns a method which takes the source store as parameter and extracts the order creation point of sale country code
 */
export const retrieveOrderCreationPointOfSaleCountryCode = () => (source: Observable<OrderStore>) =>
  source.pipe(
    filter((store) => !!store.order),
    select(selectCurrentOrder),
    map((order) => order?.creationPointOfSale?.countryCode || ''),
    distinctUntilChanged()
  );

/**
 * Returns a method which takes the source store as parameter and extracts the order servicing point of sale country code
 */
export const retrieveOrderServicingPointOfSaleCountryCode = () => (source: Observable<OrderStore>) =>
  source.pipe(
    filter((store) => !!store.order),
    select(selectCurrentOrder),
    map((order) => order?.servicingPointOfSale?.countryCode || ''),
    distinctUntilChanged()
  );

/**
 * Returns a method which takes the source store as parameter and extracts the number of hours since the order was created
 * The number of hours is calculated as the difference between the current date and the order creation date only once.
 */
export const retrieveOrderHoursSinceCreation = () => (source: Observable<OrderStore>) =>
  source.pipe(
    filter((store) => !!store.order),
    select(selectCurrentOrder),
    filter<OrderModel>((order) => !!order?.creationDateTime),
    map((order) => {
      const now = new Date();
      const timezoneOffset = now.getTimezoneOffset();
      // The timezone offset is added to the difference to account for the timezone which is added to creationDateTime on session storage data rehydration in the browser
      return Math.floor(((now.getTime() - order.creationDateTime.getTime()) / 1000 / 60 / 60) + (timezoneOffset / 60));
    }),
    take(1)
  );
